import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useNavigate, Link } from 'react-router';
import { useInviteUser, useLocations, useTeamLeads } from '@t90/api-client';
import type { Location } from '@t90/common/supabase';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Input,
  Button,
  ArrowLeft,
  Mail,
  UserPlus,
} from '@t90/ui';

const ROLES = [
  { value: 'Admin', label: 'Admin', description: 'Full system access' },
  { value: 'Exec', label: 'Executive', description: 'View all reports and metrics' },
  { value: 'Ops Manager', label: 'Operations Manager', description: 'Manage orders, projects, and staff' },
  { value: 'Team Lead', label: 'Team Lead', description: 'Manage assigned trainees and production' },
  { value: 'Program Staff', label: 'Program Staff', description: 'Basic production and quality tasks' },
  { value: 'Finance', label: 'Finance', description: 'Billing and financial reports' },
];

const inviteFormSchema = z.object({
  email: z.string().email('Please enter a valid email address'),
  full_name: z.string().min(2, 'Name must be at least 2 characters').optional().or(z.literal('')),
  role: z.string().min(1, 'Please select a role'),
  location_id: z.string().uuid('Please select a location'),
  team_lead_id: z.string().uuid('Please select a team lead').optional().or(z.literal('')),
});

type InviteFormValues = z.infer<typeof inviteFormSchema>;

export default function InviteUserPage() {
  const navigate = useNavigate();
  const inviteUser = useInviteUser();
  const { data: locations, isLoading: locationsLoading } = useLocations() as { data: Location[] | undefined; isLoading: boolean };

  const form = useForm<InviteFormValues>({
    resolver: zodResolver(inviteFormSchema),
    defaultValues: {
      email: '',
      full_name: '',
      role: '',
      location_id: '',
      team_lead_id: '',
    },
  });

  const selectedLocationId = form.watch('location_id');
  const selectedRole = form.watch('role');
  
  const { data: teamLeads, isLoading: teamLeadsLoading } = useTeamLeads(selectedLocationId || undefined);

  const showTeamLeadField = selectedRole === 'Program Staff';

  async function onSubmit(data: InviteFormValues) {
    try {
      await inviteUser.mutateAsync({
        email: data.email,
        role: data.role,
        location_id: data.location_id,
        team_lead_id: data.team_lead_id || undefined,
        full_name: data.full_name || undefined,
      });
      navigate('/settings/users');
    } catch (error) {
      // Error is handled by the mutation
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" asChild>
          <Link to="/settings/users">
            <ArrowLeft className="h-4 w-4" />
          </Link>
        </Button>
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Invite User</h1>
          <p className="text-muted-foreground">
            Send an invitation to join the Operations Hub
          </p>
        </div>
      </div>

      <Card className="max-w-2xl">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <UserPlus className="h-5 w-5" />
            New User Invitation
          </CardTitle>
          <CardDescription>
            The user will receive an email with a link to set up their account. 
            The invitation expires in 24 hours.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Email Address *</FormLabel>
                    <FormControl>
                      <div className="relative">
                        <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                        <Input 
                          type="email" 
                          placeholder="user@turn90.org" 
                          className="pl-10"
                          {...field} 
                        />
                      </div>
                    </FormControl>
                    <FormDescription>
                      Must be a @turn90.org email address
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="full_name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Full Name</FormLabel>
                    <FormControl>
                      <Input placeholder="John Smith" {...field} />
                    </FormControl>
                    <FormDescription>
                      Optional - can be set by the user when they accept the invite
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="role"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Role *</FormLabel>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select a role" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {ROLES.map((role) => (
                          <SelectItem key={role.value} value={role.value}>
                            <div className="flex flex-col">
                              <span>{role.label}</span>
                              <span className="text-xs text-muted-foreground">
                                {role.description}
                              </span>
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="location_id"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Location *</FormLabel>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select a location" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {locationsLoading ? (
                          <SelectItem value="" disabled>Loading...</SelectItem>
                        ) : (
                          locations?.map((location) => (
                            <SelectItem key={location.id} value={location.id}>
                              {location.location_name}
                            </SelectItem>
                          ))
                        )}
                      </SelectContent>
                    </Select>
                    <FormDescription>
                      Primary work location for this user
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {showTeamLeadField && (
                <FormField
                  control={form.control}
                  name="team_lead_id"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Team Lead</FormLabel>
                      <Select 
                        onValueChange={field.onChange} 
                        value={field.value}
                        disabled={!selectedLocationId}
                      >
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder={
                              !selectedLocationId 
                                ? "Select a location first" 
                                : "Select team lead (optional)"
                            } />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {teamLeadsLoading ? (
                            <SelectItem value="" disabled>Loading...</SelectItem>
                          ) : teamLeads?.length === 0 ? (
                            <SelectItem value="" disabled>No team leads at this location</SelectItem>
                          ) : (
                            teamLeads?.map((tl) => (
                              <SelectItem key={tl.id} value={tl.id}>
                                {tl.user?.full_name || tl.user?.email || 'Unknown'}
                              </SelectItem>
                            ))
                          )}
                        </SelectContent>
                      </Select>
                      <FormDescription>
                        Assign this staff member to a team lead
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              )}

              <div className="flex gap-4 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => navigate('/settings/users')}
                >
                  Cancel
                </Button>
                <Button type="submit" disabled={inviteUser.isPending}>
                  {inviteUser.isPending ? (
                    <>Sending Invitation...</>
                  ) : (
                    <>
                      <Mail className="mr-2 h-4 w-4" />
                      Send Invitation
                    </>
                  )}
                </Button>
              </div>
            </form>
          </Form>
        </CardContent>
      </Card>
    </div>
  );
}
